import { useTranslate } from 'module/hooks/use-translation';
import { withExternalFilter } from 'module/ui/components/atoms/grid';
import { Grid as BaseGrid } from 'module/ui/components/atoms/grid/grid';
import { SearchInputExpanded } from 'module/ui/components/atoms/input/search-input-expanded';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { Heading01, SingleLineEllipsis } from 'module/ui/components/styles';
import {
  faCalendarLines,
  faEnvelope,
  faFileLines,
} from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '@rocketlane/ui';
import { ColDef, SelectionChangedEvent } from 'ag-grid-community';
import { PageTemplateLite, PageType } from 'api/page-template/types';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { sortByText } from 'utils/sort-by-text';
import { getAiPageType } from '../file-viewer/icon-for-file-type';

const Label = styled.div`
  ${Heading01};
`;
const QuickFilters = styled.div`
  display: flex;
  gap: var(--spacing-03);
  padding: var(--spacing-03) 0;
`;
const QuickFilter = styled.div<{ $active: boolean }>`
  border: 1px solid var(--gray-20);
  border-radius: var(--border-radius-default);
  cursor: pointer;
  padding: 0 var(--spacing-03);
  height: 24px;
  display: flex;
  align-items: center;
  font-size: var(--font-size-sm);
  ${({ $active }) =>
    $active &&
    css`
      border: 1px solid var(--ozone-blue-surface);
      background: var(--ozone-blue-surface);
      color: var(--ozone-blue);
    `};
`;
const GridWithFilter = withExternalFilter(BaseGrid);
const Grid = styled(GridWithFilter)`
  .ag-header {
    display: none;
  }
  flex: 1;
  height: 100%;
  .ag-cell {
    border: none !important;
  }
  .ag-cell-focus {
    cursor: pointer;
    border: none !important;
    background-color: transparent !important;
  }
  .ag-selection-checkbox {
    opacity: 1;
  }
  [col-id='checkbox'] {
    padding: 0 !important;
    padding-left: var(--spacing-03) !important;
  }
  --ag-cell-widget-spacing: 16px;
  --ag-row-group-indent-size: 16px;
`;
const NameCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const NameIcon = styled(Icon)`
  flex-shrink: 0;
`;
const Name = styled.div`
  ${SingleLineEllipsis};
`;

const NameCell = ({ data }: { data: any }) => {
  const icon = useMemo(() => {
    switch (data.type) {
      case PageType.Company:
        return faFileLines;
      case PageType.Meeting:
        return faCalendarLines;
      case PageType.Email:
        return faEnvelope;
      default:
        return faFileLines;
    }
  }, [data.type]);

  return (
    <NameCellWrapper>
      <NameIcon icon={icon} size=".75x" />
      <Name>{data.name}</Name>
    </NameCellWrapper>
  );
};
const TypeCell = ({ data }: { data: any }) => {
  const translate = useTranslate();
  const value = useMemo(
    () => getAiPageType(data.type, translate),
    [data.type, translate],
  );

  return <div>{value}</div>;
};
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
  height: 100%;
  overflow: hidden;
`;

interface Props {
  data: PageTemplateLite[];
  onSelectionChange: (selectedIds: number[]) => void;
}

export const PageTemplatesGrid: FC<Props> = ({ data, onSelectionChange }) => {
  const {
    value: searchText,
    onChange: onSearchChange,
    clear: clearSearch,
  } = useSearchValueState();
  const [templateType, setTemplateType] = useState<PageType | undefined>(
    undefined,
  );
  const templateTypes = useMemo(
    () => [
      {
        label: <FormattedMessage id="all" />,
        value: undefined,
      },
      {
        label: (
          <FormattedMessage id="knowledge_base.ai_pages.grid.quick_filter.company" />
        ),
        value: PageType.Company,
      },
      {
        label: (
          <FormattedMessage id="knowledge_base.ai_pages.grid.quick_filter.meeting" />
        ),
        value: PageType.Meeting,
      },
      {
        label: (
          <FormattedMessage id="knowledge_base.ai_pages.grid.quick_filter.email" />
        ),
        value: PageType.Email,
      },
    ],
    [],
  );
  const rowData = useMemo(() => {
    return data
      .map((item) => ({
        id: item.templateId,
        name: item.templateName,
        type: item.pageType,
      }))
      .sort((a, b) => sortByText(a.name, b.name));
  }, [data]);

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        field: 'checkbox',
        headerName: '',
        pinned: 'left',
        lockPinned: true,
        lockPosition: true,
        initialWidth: 24,
        resizable: false,
        sortable: false,
        editable: false,
        checkboxSelection: true,
        headerCheckboxSelection: false,
        headerCheckboxSelectionFilteredOnly: true,
      },
      {
        field: 'name',
        headerName: '',
        flex: 1,
        cellRenderer: NameCell,
      },
      {
        width: 200,
        headerName: '',
        field: 'type',
        cellRenderer: TypeCell,
      },
    ],
    [],
  );
  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedIds = selectedNodes.map((node) => node.data.id);

    onSelectionChange?.(selectedIds);
  };
  const filter = useCallback(
    (data) => {
      return (
        data.name.toLowerCase().includes(searchText.toLowerCase()) &&
        (templateType ? data.type === templateType : true)
      );
    },
    [templateType, searchText],
  );

  return (
    <Wrapper>
      <Label>
        <FormattedMessage id="knowledge_base.ai_pages.grid.label" />
      </Label>
      <SearchInputExpanded
        value={searchText}
        onChange={onSearchChange}
        onClear={clearSearch}
      />
      <QuickFilters>
        {templateTypes.map((type) => (
          <QuickFilter
            $active={templateType === type.value}
            key={type.value}
            onClick={() => setTemplateType(type.value)}
          >
            {type.label}
          </QuickFilter>
        ))}
      </QuickFilters>
      <Grid
        filter={filter}
        rowData={rowData}
        columnDefs={columnDefs}
        onSelectionChanged={handleSelectionChanged}
        defaultColDef={{
          filter: false,
          sortable: false,
          resizable: false,
          wrapText: true,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
        }}
        rowSelection="multiple"
        rowMultiSelectWithClick
        suppressContextMenu
      />
    </Wrapper>
  );
};
